import type { IdoRelationFilterEntry } from 'src/stores/idoActivityHistoryList/types';

import { nextTick } from 'vue';

import { warmupTimerList } from 'src/stores/appointment/eventSubscriptions';
import { fetchAgendaList } from 'src/stores/appointmentModuleSearch/eventSubscriptions';
import { useDocumentStore } from 'src/stores/document';
import { useDocumentListStore } from 'src/stores/documentList';
import {
	deleteTheSelectedDocumentAggregateIds,
	fetchDocumentList,
	setTheStateForTheSelectedDocumentAggregateIds,
	warmupDocumentList,
} from 'src/stores/documentList/eventSubscriptions';
import { useFilterStore } from 'src/stores/filter';
import {
	deleteTheSelectedFilterListAggregateIds,
	fetchFilterList,
	setTheStateForTheSelectedFilterAggregateIds,
	warmupFilterList,
} from 'src/stores/filterList/eventSubscriptions';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import {
	deleteTheSelectedIdoHistoryAggregateIds,
	fetchIdoActivityHistoryList,
	fetchIdoActivityHistoryListByIdoAndInstanceId,
	setTheStateForTheSelectedIdoHistoryAggregateIds,
	unsubscribeFetchIdoActivityHistoryListByIdoAndInstanceId,
	unsubscribeForIdoActivityHistoryListUpdates,
} from 'src/stores/idoActivityHistoryList/eventSubscriptions';
import {
	fetchIdoPrintListByIdoAndInstanceId,
	setTheStateForTheSelectedPrintAggregateIds,
} from 'src/stores/idoPrintList/eventSubscriptions';
import {
	deleteTheSelectedInboxAggregateIds,
	fetchInbox,
	setTheStateForTheSelectedInboxAggregateIds,
	warmupInboxList,
} from 'src/stores/inbox/eventSubscriptions';
import { useMailStore } from 'src/stores/mail';
import { useMailListStore } from 'src/stores/mailList';
import {
	deleteTheSelectedMailAggregateIds,
	fetchMailList,
	setTheStateForTheSelectedMailAggregateIds,
	spamTheSelectedMailAggregateIds,
	warmupMailList,
} from 'src/stores/mailList/eventSubscriptions';
import { useMemoStore } from 'src/stores/memo';
import { warmupUnfinishedElementCount } from 'src/stores/menu/eventSubscriptions';
import { useMessageStore } from 'src/stores/message';
import { useMessageListStore } from 'src/stores/messageList';
import {
	fetchMessageList,
	setTheStateForTheSelectedMessageAggregateIds,
	warmupMessageList,
} from 'src/stores/messageList/eventSubscriptions';
import { useNoteStore } from 'src/stores/note';
import { useNoteListStore } from 'src/stores/noteList';
import {
	deleteTheSelectedNoteAggregateIds,
	fetchNoteList,
	setTheStateForTheSelectedNoteAggregateIds,
	warmupNoteList,
} from 'src/stores/noteList/eventSubscriptions';
import { usePhoneCallStore } from 'src/stores/phoneCall';
import { usePhoneCallListStore } from 'src/stores/phoneCallList';
import {
	fetchPhoneCallList,
	setTheStateForTheSelectedPhoneCallAggregateIds,
	warmupPhoneCallList,
} from 'src/stores/phoneCallList/eventSubscriptions';
import { useStatusStore } from 'src/stores/status';
import { resetStatusWritesOnLogout } from 'src/stores/status/statusWrites';
import { useTaskStore } from 'src/stores/task';
import { useTaskListStore } from 'src/stores/taskList';
import {
	deleteTheSelectedTaskAggregateIds,
	fetchTaskList,
	setTheStateForTheSelectedTaskAggregateIds,
	warmupTaskList,
} from 'src/stores/taskList/eventSubscriptions';
import { useTrashListStore } from 'src/stores/trashList';
import {
	fetchTrashList,
	setTheStateForTheSelectedTrashAggregateIds,
	warmupTrashList,
} from 'src/stores/trashList/eventSubscriptions';
import { RuntimeStoreContext } from 'src/types/runtime/piniaRuntimeContext';

import { DateRangeSelection } from 'src/models/common';
import { ActivityListTypeEnum, MODULE } from 'src/models/genericActivityList';
import { IdoButton } from 'src/models/ido';
import { ModuleNameEnum } from 'src/models/module/enums';
import { StatusEnum } from 'src/models/status/enums';

import { theCollectionHasAtLeastOneAggregateForTheModule } from 'src/util/aggregate/AggregateUtilityFunctions';
import useRecentlyAggregateIdsSetToReady from 'src/util/collection/recentlyAggregateIdsSetToReady';
import { CommandBus } from 'src/util/command/CommandBus';
import { EventBus } from 'src/util/eventBus/EventBus';
import { foregroundRefreshGate } from 'src/util/foreground/foregroundRefreshGate';
import { resolveListInstance } from 'src/util/listInstance/listInstanceFetchDispatch';
import { listInstanceRegistry } from 'src/util/listInstance/listInstanceRegistry';
import { QueryStore } from 'src/util/query/QueryStore';

export const initializeEventBusSubscriptions = (
	eventBus: EventBus,
	store: RuntimeStoreContext,
	queryStore: QueryStore,
	commandBus: CommandBus
) => {
	const documentListStore = useDocumentListStore();
	const filterStore = useFilterStore();
	const genericActivityListStore = useGenericActivityListStore();
	const mailListStore = useMailListStore();
	const messageListStore = useMessageListStore();
	const noteListStore = useNoteListStore();
	const phoneCallListStore = usePhoneCallListStore();
	const taskListStore = useTaskListStore();
	const trashListStore = useTrashListStore();
	const statusStore = useStatusStore();
	const hasActiveModuleSearch = (module: MODULE): boolean => {
		return filterStore.searchTerm(module).trim() !== '' || filterStore.hiddenSearchTerm(module).trim() !== '';
	};
	const resolveModuleSearchTerms = (module: MODULE): { hiddenSearchTerm: string; searchTerm: string } => {
		return {
			hiddenSearchTerm: filterStore.hiddenSearchTerm(module),
			searchTerm: filterStore.searchTerm(module),
		};
	};

	eventBus.on(
		'warmupLists',
		(queryParameters: {
			clearResultsBeforeFetch?: boolean;
			desiredDate: DateRangeSelection | string;
			hiddenSearchTerm?: string;
			searchTerm?: string;
		}) => {
			if (!foregroundRefreshGate.shouldRefreshAfterForeground()) {
				return;
			}

			const activeModule = genericActivityListStore.activeModule;

			void warmupUnfinishedElementCount(store, queryStore);

			if (!hasActiveModuleSearch(MODULE.INBOX) && activeModule !== MODULE.INBOX) {
				void warmupInboxList(store, queryStore);
			}

			const documentListDate = documentListStore.dateFilter as DateRangeSelection | string;

			if (!hasActiveModuleSearch(MODULE.DOCUMENT_LIST) && activeModule !== MODULE.DOCUMENT_LIST) {
				void warmupDocumentList(store, queryStore, {
					activityListType: ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE,
					desiredDate:
						(typeof documentListDate === 'string' && documentListDate !== '') ||
						(typeof documentListDate === 'object' && 'from' in documentListDate)
							? documentListDate
							: queryParameters.desiredDate,
					...resolveModuleSearchTerms(MODULE.DOCUMENT_LIST),
				});
			}

			const mailListDate = mailListStore.dateFilter as DateRangeSelection | string;

			if (!hasActiveModuleSearch(MODULE.MAIL_LIST) && activeModule !== MODULE.MAIL_LIST) {
				void warmupMailList(store, queryStore, {
					activityListType: ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE,
					desiredDate:
						(typeof mailListDate === 'string' && mailListDate !== '') ||
						(typeof mailListDate === 'object' && 'from' in mailListDate)
							? mailListDate
							: queryParameters.desiredDate,
					...resolveModuleSearchTerms(MODULE.MAIL_LIST),
				});
			}

			const messageListDate = messageListStore.dateFilter as DateRangeSelection | string;

			if (!hasActiveModuleSearch(MODULE.MESSAGE_LIST) && activeModule !== MODULE.MESSAGE_LIST) {
				void warmupMessageList(store, queryStore, {
					activityListType: ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE,
					desiredDate:
						(typeof messageListDate === 'string' && messageListDate !== '') ||
						(typeof messageListDate === 'object' && 'from' in messageListDate)
							? messageListDate
							: queryParameters.desiredDate,
					...resolveModuleSearchTerms(MODULE.MESSAGE_LIST),
				});
			}

			const noteListDate = noteListStore.dateFilter as DateRangeSelection | string;

			if (!hasActiveModuleSearch(MODULE.NOTE_LIST) && activeModule !== MODULE.NOTE_LIST) {
				void warmupNoteList(store, queryStore, {
					activityListType: ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE,
					desiredDate:
						(typeof noteListDate === 'string' && noteListDate !== '') ||
						(typeof noteListDate === 'object' && 'from' in noteListDate)
							? noteListDate
							: queryParameters.desiredDate,
					...resolveModuleSearchTerms(MODULE.NOTE_LIST),
				});
			}

			const phoneCallListDate = phoneCallListStore.dateFilter as DateRangeSelection | string;

			if (!hasActiveModuleSearch(MODULE.PHONE_CALL_LIST) && activeModule !== MODULE.PHONE_CALL_LIST) {
				void warmupPhoneCallList(store, queryStore, {
					activityListType: ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE,
					desiredDate:
						(typeof phoneCallListDate === 'string' && phoneCallListDate !== '') ||
						(typeof phoneCallListDate === 'object' && 'from' in phoneCallListDate)
							? phoneCallListDate
							: queryParameters.desiredDate,
					...resolveModuleSearchTerms(MODULE.PHONE_CALL_LIST),
				});
			}

			const taskListDate = taskListStore.dateFilter as DateRangeSelection | string;

			if (!hasActiveModuleSearch(MODULE.TASK_LIST) && activeModule !== MODULE.TASK_LIST) {
				void warmupTaskList(store, queryStore, {
					activityListType: ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE,
					desiredDate:
						(typeof taskListDate === 'string' && taskListDate !== '') ||
						(typeof taskListDate === 'object' && 'from' in taskListDate)
							? taskListDate
							: queryParameters.desiredDate,
					...resolveModuleSearchTerms(MODULE.TASK_LIST),
				});
			}

			const trashListDate = trashListStore.dateFilter as DateRangeSelection | string;

			if (!hasActiveModuleSearch(MODULE.TRASH_LIST) && activeModule !== MODULE.TRASH_LIST) {
				void warmupTrashList(store, queryStore, {
					activityListType: ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE,
					desiredDate:
						(typeof trashListDate === 'string' && trashListDate !== '') ||
						(typeof trashListDate === 'object' && 'from' in trashListDate)
							? trashListDate
							: queryParameters.desiredDate,
					...resolveModuleSearchTerms(MODULE.TRASH_LIST),
				});
			}

			if (!hasActiveModuleSearch(MODULE.FILTER_LIST) && activeModule !== MODULE.FILTER_LIST) {
				void warmupFilterList(store, queryStore);
			}

			void warmupTimerList(store, queryStore);
		}
	);

	eventBus.on('warmupInboxList', () => {
		void warmupInboxList(store, queryStore);
	});

	eventBus.on('fetchDocumentList', (params: Record<string, unknown>) => {
		const resolved = resolveListInstance(params['genericListIdentifier'] as string | undefined);

		if (resolved.outcome === 'missing') {
			return; // MUST-FIX F: verspäteter Fetch aus geschlossenem Tab — NICHT auf den Klassik-Store schreiben.
		}

		void fetchDocumentList(
			store,
			(resolved.outcome === 'instance' ? resolved.queryStore : queryStore) as never,
			params as never,
			(resolved.outcome === 'instance' ? resolved.listStore : undefined) as never // undefined ⇒ Helper nutzt use<Module>Store() (Default)
		);
	});

	eventBus.on('fetchInbox', (params: Record<string, unknown> = {}) => {
		const resolved = resolveListInstance(params['genericListIdentifier'] as string | undefined);

		if (resolved.outcome === 'missing') {
			return; // MUST-FIX F: verspäteter Fetch aus geschlossenem Tab — NICHT auf den Klassik-Store schreiben.
		}

		void fetchInbox(
			store,
			(resolved.outcome === 'instance' ? resolved.queryStore : queryStore) as never,
			(resolved.outcome === 'instance' ? resolved.listStore : undefined) as never // undefined ⇒ Helper nutzt use<Module>Store() (Default)
		);
	});

	eventBus.on(
		'fetchFilterList',
		(queryParameters: {
			activityListType: ActivityListTypeEnum;
			clearResultsBeforeFetch: boolean;
			desiredDate: DateRangeSelection | string;
			hiddenSearchTerm: string;
			searchTerm: string;
			view: string;
		}) => {
			void fetchFilterList(store, queryStore, {
				activityListType: queryParameters.activityListType,
				clearResultsBeforeFetch: queryParameters.clearResultsBeforeFetch,
				desiredDate: queryParameters.desiredDate,
				hiddenSearchTerm: queryParameters.hiddenSearchTerm,

				searchTerm: filterStore.searchTerm(MODULE.FILTER_LIST),
				view: queryParameters.view,
			});
		}
	);

	eventBus.on('fetchMailList', (params: Record<string, unknown>) => {
		const resolved = resolveListInstance(params['genericListIdentifier'] as string | undefined);

		if (resolved.outcome === 'missing') {
			return; // MUST-FIX F: verspäteter Fetch aus geschlossenem Tab — NICHT auf den Klassik-Store schreiben.
		}

		void fetchMailList(
			store,
			(resolved.outcome === 'instance' ? resolved.queryStore : queryStore) as never,
			params as never,
			(resolved.outcome === 'instance' ? resolved.listStore : undefined) as never // undefined ⇒ Helper nutzt use<Module>Store() (Default)
		);
	});

	eventBus.on('fetchMessageList', (params: Record<string, unknown>) => {
		const resolved = resolveListInstance(params['genericListIdentifier'] as string | undefined);

		if (resolved.outcome === 'missing') {
			return; // MUST-FIX F: verspäteter Fetch aus geschlossenem Tab — NICHT auf den Klassik-Store schreiben.
		}

		void fetchMessageList(
			store,
			(resolved.outcome === 'instance' ? resolved.queryStore : queryStore) as never,
			params as never,
			(resolved.outcome === 'instance' ? resolved.listStore : undefined) as never // undefined ⇒ Helper nutzt use<Module>Store() (Default)
		);
	});

	eventBus.on('fetchNoteList', (params: Record<string, unknown>) => {
		const resolved = resolveListInstance(params['genericListIdentifier'] as string | undefined);

		if (resolved.outcome === 'missing') {
			return; // MUST-FIX F: verspäteter Fetch aus geschlossenem Tab — NICHT auf den Klassik-Store schreiben.
		}

		void fetchNoteList(
			store,
			(resolved.outcome === 'instance' ? resolved.queryStore : queryStore) as never,
			params as never,
			(resolved.outcome === 'instance' ? resolved.listStore : undefined) as never // undefined ⇒ Helper nutzt use<Module>Store() (Default)
		);
	});

	eventBus.on('fetchPhoneCallList', (params: Record<string, unknown>) => {
		const resolved = resolveListInstance(params['genericListIdentifier'] as string | undefined);

		if (resolved.outcome === 'missing') {
			return; // MUST-FIX F: verspäteter Fetch aus geschlossenem Tab — NICHT auf den Klassik-Store schreiben.
		}

		void fetchPhoneCallList(
			store,
			(resolved.outcome === 'instance' ? resolved.queryStore : queryStore) as never,
			params as never,
			(resolved.outcome === 'instance' ? resolved.listStore : undefined) as never // undefined ⇒ Helper nutzt use<Module>Store() (Default)
		);
	});

	eventBus.on('fetchTaskList', (params: Record<string, unknown>) => {
		const resolved = resolveListInstance(params['genericListIdentifier'] as string | undefined);

		if (resolved.outcome === 'missing') {
			return; // MUST-FIX F: verspäteter Fetch aus geschlossenem Tab — NICHT auf den Klassik-Store schreiben.
		}

		void fetchTaskList(
			store,
			(resolved.outcome === 'instance' ? resolved.queryStore : queryStore) as never,
			params as never,
			(resolved.outcome === 'instance' ? resolved.listStore : undefined) as never // undefined ⇒ Helper nutzt use<Module>Store() (Default)
		);
	});

	eventBus.on('fetchTrashList', (params: Record<string, unknown>) => {
		const resolved = resolveListInstance(params['genericListIdentifier'] as string | undefined);

		if (resolved.outcome === 'missing') {
			return; // MUST-FIX F: verspäteter Fetch aus geschlossenem Tab — NICHT auf den Klassik-Store schreiben.
		}

		void fetchTrashList(
			store,
			(resolved.outcome === 'instance' ? resolved.queryStore : queryStore) as never,
			params as never,
			(resolved.outcome === 'instance' ? resolved.listStore : undefined) as never // undefined ⇒ Helper nutzt use<Module>Store() (Default)
		);
	});

	eventBus.on('fetchAgendaList', (params: Record<string, unknown>) => {
		const resolved = resolveListInstance(params['genericListIdentifier'] as string | undefined);

		if (resolved.outcome === 'missing') {
			return; // MUST-FIX F: verspäteter Fetch aus geschlossenem Tab — NICHT auf den Klassik-Store schreiben.
		}

		void fetchAgendaList(
			store,
			(resolved.outcome === 'instance' ? resolved.queryStore : queryStore) as never,
			params as never,
			(resolved.outcome === 'instance' ? resolved.listStore : undefined) as never // undefined ⇒ Helper nutzt use<Module>Store() (Default)
		);
	});

	eventBus.on(
		'fetchIdoActivityHistoryList',
		(
			uniqueListIdentifier: string,
			queryParameters: {
				activityListType: ActivityListTypeEnum;
				clearResultsBeforeFetch: boolean;
				hiddenSearchTerm: string;
				idoButton: IdoButton;
				searchTerm: string;
			}
		) => {
			void fetchIdoActivityHistoryList(uniqueListIdentifier, store, queryStore, {
				activityListType: queryParameters.activityListType,
				clearResultsBeforeFetch: queryParameters.clearResultsBeforeFetch,
				hiddenSearchTerm: queryParameters.hiddenSearchTerm,
				idoButton: queryParameters.idoButton,
				searchTerm: queryParameters.searchTerm,
			});
		}
	);

	eventBus.on(
		'fetchIdoActivityHistoryListByIdoAndInstanceId',
		(
			uniqueListIdentifier: string,
			queryParameters: {
				activityListType: ActivityListTypeEnum;
				aggregateId: string;
				clearResultsBeforeFetch: boolean;
				hiddenSearchTerm: string;
				idoId: number;
				idoInstanceId: number;
				idoRelations?: Array<IdoRelationFilterEntry>;
				searchTerm: string;
			}
		) => {
			void fetchIdoActivityHistoryListByIdoAndInstanceId(uniqueListIdentifier, store, queryStore, {
				activityListType: queryParameters.activityListType,
				aggregateId: queryParameters.aggregateId,
				clearResultsBeforeFetch: queryParameters.clearResultsBeforeFetch,
				hiddenSearchTerm: queryParameters.hiddenSearchTerm,
				idoId: queryParameters.idoId,
				idoInstanceId: queryParameters.idoInstanceId,
				// exactOptionalPropertyTypes: kein explizites undefined an das optionale Feld reichen.
				...(queryParameters.idoRelations === undefined ? {} : { idoRelations: queryParameters.idoRelations }),
				searchTerm: queryParameters.searchTerm,
			});
		}
	);

	eventBus.on(
		'fetchIdoPrintListByIdoAndInstanceId',
		(
			uniqueListIdentifier: string,
			queryParameters: {
				idoId: number;
				instanceId: number;
			}
		) => {
			void fetchIdoPrintListByIdoAndInstanceId(uniqueListIdentifier, store, queryStore, {
				idoId: queryParameters.idoId,
				instanceId: queryParameters.instanceId,
			});
		}
	);

	eventBus.on(
		'unsubscribeFetchIdoActivityHistoryListByIdoAndInstanceId',
		(
			uniqueListIdentifier: string,
			queryParameters: {
				activityListType: ActivityListTypeEnum;
				aggregateId: string;
				hiddenSearchTerm: string;
				idoId: number;
				idoInstanceId: number;
				idoRelations?: Array<IdoRelationFilterEntry>;
				searchTerm: string;
			}
		) => {
			void unsubscribeFetchIdoActivityHistoryListByIdoAndInstanceId(uniqueListIdentifier, store, queryStore, {
				activityListType: queryParameters.activityListType,
				aggregateId: queryParameters.aggregateId,
				hiddenSearchTerm: queryParameters.hiddenSearchTerm,
				idoId: queryParameters.idoId,
				idoInstanceId: queryParameters.idoInstanceId,
				// exactOptionalPropertyTypes: kein explizites undefined an das optionale Feld reichen.
				...(queryParameters.idoRelations === undefined ? {} : { idoRelations: queryParameters.idoRelations }),
				searchTerm: queryParameters.searchTerm,
			});
		}
	);

	eventBus.on(
		'unsubscribeForIdoActivityHistoryListUpdates',
		(
			uniqueListIdentifier: string,
			queryParameters: {
				activityListType: ActivityListTypeEnum;
				hiddenSearchTerm: string;
				idoButton: IdoButton;
				searchTerm: string;
			}
		) => {
			void unsubscribeForIdoActivityHistoryListUpdates(uniqueListIdentifier, store, queryStore, {
				activityListType: queryParameters.activityListType,
				hiddenSearchTerm: queryParameters.hiddenSearchTerm,
				idoButton: queryParameters.idoButton,
				searchTerm: queryParameters.searchTerm,
			});
		}
	);

	eventBus.on(
		'deleteTheSelectedAggregateIds',
		// genericListIdentifier optional & pass-through: die per-Modul-Funktionen lesen es bereits selbst
		// via `queryParameters['genericListIdentifier'] ?? '<module>'` (Phase 2). Fehlt es (Klassik/ältere
		// Emitter), bleibt das Verhalten byte-identisch zum reinen Modulnamen-Default.
		(queryParameters: { aggregateIds: string[]; genericListIdentifier?: string; processId: string }) => {
			void deleteTheSelectedInboxAggregateIds(store, queryStore, queryParameters);

			void deleteTheSelectedFilterListAggregateIds(store, queryStore, queryParameters);

			void deleteTheSelectedIdoHistoryAggregateIds(store, queryStore, queryParameters);

			if (
				theCollectionHasAtLeastOneAggregateForTheModule(
					queryParameters.aggregateIds,
					ModuleNameEnum.MODULE_EMAIL
				)
			) {
				void deleteTheSelectedMailAggregateIds(store, queryStore, queryParameters);
			}

			if (
				theCollectionHasAtLeastOneAggregateForTheModule(
					queryParameters.aggregateIds,
					ModuleNameEnum.MODULE_DOCUMENT
				)
			) {
				void deleteTheSelectedDocumentAggregateIds(store, queryStore, queryParameters);
			}

			if (
				theCollectionHasAtLeastOneAggregateForTheModule(
					queryParameters.aggregateIds,
					ModuleNameEnum.MODULE_NOTE
				)
			) {
				void deleteTheSelectedNoteAggregateIds(store, queryStore, queryParameters);
			}

			if (
				theCollectionHasAtLeastOneAggregateForTheModule(
					queryParameters.aggregateIds,
					ModuleNameEnum.MODULE_TASK
				)
			) {
				void deleteTheSelectedTaskAggregateIds(store, queryStore, queryParameters);
			}

			void nextTick(() => {
				eventBus.emit('recalculateTheGroupingHeader');
			});
		}
	);

	eventBus.on(
		'spamTheSelectedAggregateIds',
		// s. deleteTheSelectedAggregateIds oben: genericListIdentifier optional & pass-through.
		(queryParameters: { aggregateIds: string[]; genericListIdentifier?: string; processId: string }) => {
			void deleteTheSelectedInboxAggregateIds(store, queryStore, queryParameters);

			void deleteTheSelectedFilterListAggregateIds(store, queryStore, queryParameters);

			void deleteTheSelectedIdoHistoryAggregateIds(store, queryStore, queryParameters);

			void spamTheSelectedMailAggregateIds(store, queryStore, queryParameters);

			void nextTick(() => {
				eventBus.emit('recalculateTheGroupingHeader');
			});
		}
	);

	eventBus.on(
		'setTheStateForTheSelectedAggregateIds',
		// s. deleteTheSelectedAggregateIds oben: genericListIdentifier optional & pass-through.
		(queryParameters: {
			aggregateIds: string[];
			genericListIdentifier?: string;
			processId: string;
			state: StatusEnum;
		}) => {
			const recentlyAggregateIdsSetToReady = useRecentlyAggregateIdsSetToReady();

			if (queryParameters.state == StatusEnum.READY) {
				queryParameters.aggregateIds.forEach((aggregateId) => {
					recentlyAggregateIdsSetToReady.add(aggregateId);
				});
			} else {
				queryParameters.aggregateIds.forEach((aggregateId) => {
					recentlyAggregateIdsSetToReady.delete(aggregateId);
				});
			}

			void setTheStateForTheSelectedInboxAggregateIds(store, queryStore, queryParameters);

			void setTheStateForTheSelectedIdoHistoryAggregateIds(store, queryStore, queryParameters);

			void setTheStateForTheSelectedPrintAggregateIds(store, queryStore, queryParameters);

			void setTheStateForTheSelectedFilterAggregateIds(store, queryStore, queryParameters);

			if (
				theCollectionHasAtLeastOneAggregateForTheModule(
					queryParameters.aggregateIds,
					ModuleNameEnum.MODULE_EMAIL
				)
			) {
				void setTheStateForTheSelectedMailAggregateIds(store, queryStore, queryParameters);
				void setTheStateForTheSelectedTrashAggregateIds(store, queryStore, queryParameters);
				useMailStore().applyTheStatusForTheAggregateIds({
					aggregateIds: queryParameters.aggregateIds,
					status: queryParameters.state,
				});
			}

			if (
				theCollectionHasAtLeastOneAggregateForTheModule(
					queryParameters.aggregateIds,
					ModuleNameEnum.MODULE_PHONECALL
				)
			) {
				void setTheStateForTheSelectedPhoneCallAggregateIds(store, queryStore, queryParameters);
				usePhoneCallStore().applyTheStatusForTheAggregateIds({
					aggregateIds: queryParameters.aggregateIds,
					status: queryParameters.state,
				});
			}

			if (
				theCollectionHasAtLeastOneAggregateForTheModule(
					queryParameters.aggregateIds,
					ModuleNameEnum.MODULE_TASK
				)
			) {
				void setTheStateForTheSelectedTaskAggregateIds(store, queryStore, queryParameters);
				useTaskStore().applyTheStatusForTheAggregateIds({
					aggregateIds: queryParameters.aggregateIds,
					status: queryParameters.state,
				});
			}

			if (
				theCollectionHasAtLeastOneAggregateForTheModule(
					queryParameters.aggregateIds,
					ModuleNameEnum.MODULE_MESSAGE
				)
			) {
				void setTheStateForTheSelectedMessageAggregateIds(store, queryStore, queryParameters);
				useMessageStore().applyTheStatusForTheAggregateIds({
					aggregateIds: queryParameters.aggregateIds,
					status: queryParameters.state,
				});
			}

			if (
				theCollectionHasAtLeastOneAggregateForTheModule(
					queryParameters.aggregateIds,
					ModuleNameEnum.MODULE_DOCUMENT
				)
			) {
				void setTheStateForTheSelectedDocumentAggregateIds(store, queryStore, queryParameters);
				useDocumentStore().applyTheStatusForTheAggregateIds({
					aggregateIds: queryParameters.aggregateIds,
					status: queryParameters.state,
				});
			}

			if (
				theCollectionHasAtLeastOneAggregateForTheModule(
					queryParameters.aggregateIds,
					ModuleNameEnum.MODULE_NOTE
				)
			) {
				void setTheStateForTheSelectedNoteAggregateIds(store, queryStore, queryParameters);
				useNoteStore().applyTheStatusForTheAggregateIds({
					aggregateIds: queryParameters.aggregateIds,
					status: queryParameters.state,
				});
			}

			if (
				theCollectionHasAtLeastOneAggregateForTheModule(
					queryParameters.aggregateIds,
					ModuleNameEnum.MODULE_ACTIVITY
				)
			) {
				useMemoStore().applyTheStatusForTheAggregateIds({
					aggregateIds: queryParameters.aggregateIds,
					status: queryParameters.state,
				});
			}

			if (queryParameters.state === StatusEnum.UNSEEN) {
				statusStore.setTheStatusStateToUnseen({
					aggregateIds: queryParameters.aggregateIds,
					commandBus: commandBus,
					processId: queryParameters.processId,
				});
			} else if (queryParameters.state === StatusEnum.SEEN) {
				statusStore.setTheStatusStateToSeen({
					aggregateIds: queryParameters.aggregateIds,
					commandBus: commandBus,
					processId: queryParameters.processId,
				});
			} else if (queryParameters.state === StatusEnum.READY) {
				statusStore.setTheStatusStateToReady({
					aggregateIds: queryParameters.aggregateIds,
					commandBus: commandBus,
					processId: queryParameters.processId,
				});
			}
		}
	);

	eventBus.on('logout', () => {
		resetStatusWritesOnLogout();
		// Task 3.1 (Logout-Teardown): Logout ist ein harter Seiten-Reload (App.vue,
		// watch(loggedIn) → reloadAppAfterLogout in boot/axiosAuthDeniedReason), KEIN Vue-Router-Wechsel — Vue unmountet
		// die offenen Listen-Instanz-Tabs dabei nicht geordnet, onUnmounted/disposeInstance feuert also nicht
		// zuverlässig. Dieser Handler läuft synchron innerhalb von eventBus.emit('logout')
		// (user.service.ts), deutlich vor dem harten Reload (der erst nach dem await der Logout-Requests via
		// authenticationStore.logoutState() ausgelöst wird) — genug Vorlauf für einen sauberen Teardown alle
		// offenen Nicht-Default-Instanzen, statt sie dem abrupten Seiten-Reload zu überlassen.
		listInstanceRegistry.disposeAll();
	});
};
